import { TranslationKey } from '@/contexts/translations';
import { FooterLink } from './footer-links';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useBranding } from '@/outlets/branding-outlet';
import { faqLink } from '@/util/urls';

type LinksGroupKey = 'about' | 'information' | 'needHelp';

type FooterLinksGroup = {
  label?: TranslationKey;
  links: FooterLink[];
};

type FooterConfig = {
  linksGroups: Record<LinksGroupKey, FooterLinksGroup>;
  linksGroupOrder: readonly LinksGroupKey[];
};

const getDesktopConfig = (footerLinks: Record<string, FooterLink>): FooterConfig => ({
  linksGroups: {
    about: {
      label: 'footer.about.title',
      links: [footerLinks.aboutUs, footerLinks.faq],
    },
    information: {
      label: 'footer.information.title',
      links: [footerLinks.termsOfUse, footerLinks.privacyPolicy, footerLinks.support],
    },
    needHelp: {
      links: [],
    },
  },
  linksGroupOrder: ['about', 'information'],
});

const getMobileConfig = (footerLinks: Record<string, FooterLink>): FooterConfig => ({
  linksGroups: {
    about: {
      links: [footerLinks.aboutUs],
    },
    information: {
      links: [footerLinks.privacyPolicy, footerLinks.termsOfUse],
    },
    needHelp: {
      links: [footerLinks.support],
    },
  },
  linksGroupOrder: ['needHelp', 'about', 'information'],
});

export function useFooterConfig(): FooterConfig {
  const isMobile = useMediaQuery('--screen-md');
  const { aboutUsUrl, privacyPolicyUrl, termsOfUseUrl } = useBranding();

  const footerLinks: Record<string, FooterLink> = {
    aboutUs: {
      title: 'footer.about.links.aboutUs',
      to: aboutUsUrl,
      isExternal: true,
    },
    faq: {
      title: 'footer.about.links.faq',
      to: faqLink,
      isExternal: true,
    },
    termsOfUse: {
      title: 'footer.information.links.termsOfUse',
      to: termsOfUseUrl,
      isExternal: true,
    },
    privacyPolicy: {
      title: 'footer.information.links.privacyPolicy',
      to: privacyPolicyUrl,
      isExternal: true,
    },
    support: {
      title: 'footer.information.links.contactSupport',
      to: '/support',
      isExternal: false,
    },
  };

  return isMobile ? getMobileConfig(footerLinks) : getDesktopConfig(footerLinks);
}
